import React from "react";
import { Dictionary, first, map, some } from "lodash";
import { Column, Row } from "@/shared/ui/Blocks";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { HighlightedItemsListDetailed } from "@/shared/widgets/HighlightedItemsList";
import { getTopDimensionsBySection } from "@/shared/utils/selectors/getTopDimensionsBySection";
import { Title } from "@/shared/ui/Title";
import { IconColor, IconName } from "@/shared/ui/Icon";
import { getTopBehaviouralStrengthsDimensions } from "@/shared/utils/selectors/getTopBehaviouralStrengthsDimensions";
import {
  getDictionaryTerms,
  getDimTextBucketValueFromScore,
} from "@/shared/hooks/content/useDimensionTextSelectors";
import { useDictionary } from "@/shared/hooks/content/useDictionary";
import { useTranslation } from "react-i18next";

interface DimensionsHighlightsSectionProps {
  dimensions: Dictionary<DimensionWithScores[]>;
}

export const TopDimensionsComponentsList = (
  props: DimensionsHighlightsSectionProps
) => {
  const { t } = useTranslation();
  const { dictionary } = useDictionary();
  const { dimensions } = props;
  const topDimensions = getTopDimensionsBySection(dimensions);

  const topBehaviouralStrengths = getTopBehaviouralStrengthsDimensions(
    dimensions.personality
  );

  const topBehaviouralStrengthsItems = map(topBehaviouralStrengths, (d) => {
    const textBucket = getDimTextBucketValueFromScore(d.textsScore?.value || 0);

    const strengthTitles = getDictionaryTerms(
      dictionary,
      "strength_short",
      d?.id,
      textBucket
    );
    const strengthDescription = getDictionaryTerms(
      dictionary,
      "strength",
      d?.id,
      textBucket
    );

    return {
      label: first(strengthTitles)?.text || "",
      description: first(strengthDescription)?.text || "",
      id: d.id,
    };
  });

  const listData = [
    {
      title: t("profile.highlights_top_competencies"),
      icon: "Result",
      items: topDimensions.competencies,
      iconColor: "green",
    },
    {
      title: t("profile.highlights_behavioural_strengths"),
      icon: "Strength",
      items: topBehaviouralStrengthsItems,
    },
    {
      title: t("profile.highlights_top_motivators"),
      icon: "Extraversion",
      iconColor: "orange",
      items: topDimensions.values,
    },
  ].filter((list) => list.items?.length > 0);

  return listData.map((list, index) => (
    <HighlightedItemsListDetailed
      title={list.title}
      icon={list.icon as IconName}
      iconColor={list.iconColor as IconColor}
      key={index}
      items={list.items}
    />
  ));
};

function WisnioProfileHighlightsSection(
  props: DimensionsHighlightsSectionProps
) {
  const { t } = useTranslation();
  const { dimensions } = props;

  if (some(dimensions.personality, (d) => !d.score?.value)) return null;

  return (
    <Column className="gap-4" id="profile-highlights">
      <Row className="mb-8">
        <Title as="h3" description={t("profile.highlights_description")}>
          {t("profile.highlights_title")}
        </Title>
        <Column />
      </Row>
      <Column className="gap-16 print:gap-12">
        <TopDimensionsComponentsList dimensions={dimensions} />
      </Column>
    </Column>
  );
}

export default WisnioProfileHighlightsSection;
export { WisnioProfileHighlightsSection };
